import { __ } from './translate'
import { expandArrPath } from '../utils'
import {
  findIds,
  hasLicenceToViewArticle,
  isArticleForCountry,
  isArticleForLanguage,
  isArticleForVehicleType,
  isMobileModule, prepareStringForSearch
} from '../utils/content'

export default class ContentService {
  static parsedUrl = {
    sections: [],
    articles: [],
  }

  static clear = () => {
    ContentService.parsedUrl.sections = []
    ContentService.parsedUrl.articles = []
  }

  static parseUrl = (url) => {
    ContentService.clear()
    // parse url
    // url: /sections/3/4/39/articles/93/92
    const segments = url.replace(/^\/|\/$/g, '').split('/')
    let pushTo = ''
    for (let s of segments) {
      if (s === 'sections') {
        pushTo = 'sections'
      } else if (s === 'articles') {
        pushTo = 'articles'
      } else {
        if (ContentService.parsedUrl[pushTo] === undefined) {
          throw new Error('Invalid URL pattern')
        }
        ContentService.parsedUrl[pushTo].push(Number(s))
      }
    }
    return ContentService.parsedUrl
  }

  static shouldShowSection = (url) => url.includes('sections')

  static shouldShowArticle = (url) => url.includes('articles')

  /**
   *
   * @param sectionIds Array of ids to get to nested section
   * @param langSlug
   * @param allSections
   * @returns {*}
   */
  static findSection = (sectionIds, langSlug, allSections) => {
    const ids = [...sectionIds]
    // pick section id from array
    const sectionId = ids.shift()

    // find section by first id
    const section = allSections.find(section => section.id === sectionId)

    // if not found
    if (!section) {
      throw new Error('Section not found')
    }

    // if this ID was last (sectionsIds array is empty), return final wanted section
    if (ids.length === 0) {
      return {
        id: section.id,
        name: section.name[langSlug],
        title: section.title[langSlug],
        image: section.image,
        icon: section.icon,
        sections: section.sections.map(s => ({
          id: s.id,
          name: s.name[langSlug],
          title: s.title[langSlug],
          image: s.image,
          icon: s.icon,
        })),
      }
    } else {
      // keep searching in nested sections
      return ContentService.findSection(ids, langSlug, section.sections)
    }
  }

  /**
   *
   * @param articleIds Array of ids to get to nested article
   * @param articles
   * @param vehicleType
   * @param country
   * @param userLicences
   * @returns {*}
   */
  static findArticle = (articleIds, articles, { vehicleType, country, userLicences }) => {
    const ids = [...articleIds]
    // pick article id from array
    const articleId = ids.shift()

    // find article by first id
    const article = { ...articles.find(article => article.id === articleId) }

    // if not found or has not licence to view
    if (!article || !hasLicenceToViewArticle(article, userLicences)) {
      throw new Error('Article not found')
    }

    // if this ID was last (articleIds array is empty), return final wanted article
    if (ids.length === 0) {
      if (article.categories) {
        article.categories = article.categories
          .filter(articleCategory => isArticleForCountry(articleCategory, country))
          .filter(articleCategory => isArticleForVehicleType(articleCategory, vehicleType))
      }
      return article
    } else {
      // keep searching in nested articles
      return ContentService.findArticle(ids, article.categories, { vehicleType, country, userLicences })
    }
  }

  static getArticlesForSection = (sectionId, articles, langId, licenceNumbers) => {
    return articles
      .filter(article => article.section_id === sectionId)
      .filter(article => isArticleForLanguage(article, langId))
      .filter(article => !isMobileModule(article)) // skip mobile modules
      .map(article => {
        article.disabled = !hasLicenceToViewArticle(article, licenceNumbers)
        return article
      })
  }

  static generateSectionUrl = (sectionId = null, sections = ContentService.parsedUrl.sections) => {
    let sectionIds = [...sections]

    // if sectionId is provided, append it to URL, so this means redirect
    if (sectionId !== null) {
      sectionIds.push(sectionId)
    }

    return `/sections/${sectionIds.join('/')}`
  }

  static generateArticleUrl = (articleId, data = ContentService.parsedUrl) => {
    let articleIds = [...data.articles]
    articleIds.push(articleId)
    // append sections path to url if provided
    let sectionUrl = ''
    if (data.sections.length > 0) {
      sectionUrl = ContentService.generateSectionUrl(null, data.sections)
    }
    return `${sectionUrl}/articles/${articleIds.join('/')}`
  }

  static generateBreadcrumbs = (langSlug, sections, articles, { vehicleType, country, userLicences }) => {

    // starting breadcrumb
    let breadcrumbs = [{
      name: __('Domov'),
      url: '/'
    }]

    const sectionsIds = [...ContentService.parsedUrl.sections]
    const articlesIds = [...ContentService.parsedUrl.articles]

    // load sections
    let sectionUrl = '/sections'
    expandArrPath(sectionsIds).forEach(sectionIdsPath => {
      try {
        const section = ContentService.findSection(sectionIdsPath, langSlug, sections)
        sectionUrl += `/${sectionsIds.shift()}`
        breadcrumbs.push({
          name: section.name,
          url: sectionUrl,
        })
      } catch (e) {
        return []
      }
    })

    // load articles
    let articlesUrl = '/articles'
    expandArrPath(articlesIds).forEach(articleIdsPath => {
      try {
        const article = ContentService.findArticle(articleIdsPath, articles, { vehicleType, country, userLicences })
        articlesUrl += `/${articlesIds.shift()}`
        breadcrumbs.push({
          name: article.name,
          url: `${sectionUrl}${articlesUrl}`,
        })
      } catch (e) {
        return []
      }
    })

    // add flag to last item
    if (breadcrumbs.length) {
      breadcrumbs[breadcrumbs.length - 1].last = true
    }

    return breadcrumbs
  }

  static search = (query, articles, countryId, userLicences, tree = []) => {
    let results = []
    if (!query) {
      return []
    }
    for (let article of articles) {
      if (prepareStringForSearch(article.name).indexOf(prepareStringForSearch(query)) > -1 || prepareStringForSearch(article.content).indexOf(prepareStringForSearch(query)) > -1) {
        let countries = (article.countries || []).concat(findIds(article.categories, 'countries'))
        if (!(!countryId || countries.length === 0 || countries.indexOf(countryId) > -1)) {
          continue
        }
        results.push({
          article,
          path: tree.map(article => (article.name)).join(' > '),
          url: ContentService.generateArticleUrl(article.id, {
            sections: [],
            articles: tree.map(article => article.id)
          }),
          disabled: (
            !hasLicenceToViewArticle(article, userLicences)
            || tree.filter(articleInTree => !hasLicenceToViewArticle(articleInTree, userLicences)).length > 0
          )
        })
      }
      results = results.concat(ContentService.search(query, article.categories, countryId, userLicences, tree.concat(article)))
    }
    return results
  }
}
